<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div class="card shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h1 class="h4 mb-0">{{ customer ? customer.customer_name : 'Customer' }}</h1>
              </div>

              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border" role="status"></div>
              </div>

              <div v-else-if="error" class="alert alert-danger py-2">{{ error }}</div>

              <div v-else-if="customer">
                <!-- Editable fields (supervisor only) -->
                <div v-if="isSupervisor && editing" class="mb-4">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Name</label>
                      <input v-model="editForm.customer_name" class="form-control" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Email</label>
                      <input v-model="editForm.email" type="email" class="form-control" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Phone</label>
                      <input v-model="editForm.phone" class="form-control" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Address</label>
                      <input v-model="editForm.address" class="form-control" />
                    </div>
                    <div class="col-12">
                      <label class="form-label">Notes</label>
                      <textarea v-model="editForm.notes" class="form-control" rows="2"></textarea>
                    </div>
                  </div>
                  <div class="d-flex gap-2 mt-3">
                    <button class="btn btn-primary btn-sm" @click="saveEdit" :disabled="saving">
                      <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                      Save
                    </button>
                    <button class="btn btn-outline-secondary btn-sm" @click="editing = false">Cancel</button>
                  </div>
                </div>

                <!-- Read-only display -->
                <div v-else class="mb-4">
                  <div class="row mb-2">
                    <div class="col-md-4">
                      <div class="fw-semibold">Email</div>
                      <div class="text-muted">{{ customer.email || "—" }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="fw-semibold">Phone</div>
                      <div class="text-muted">{{ customer.phone || "—" }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="fw-semibold">Created</div>
                      <div class="text-muted">{{ customer.created_at ? new Date(customer.created_at).toLocaleDateString() : "—" }}</div>
                    </div>
                  </div>
                  <div class="row mb-2" v-if="customer.address">
                    <div class="col-12">
                      <div class="fw-semibold">Address</div>
                      <div class="text-muted">{{ customer.address }}</div>
                    </div>
                  </div>
                  <div class="row mb-2" v-if="customer.notes">
                    <div class="col-12">
                      <div class="fw-semibold">Notes</div>
                      <div class="text-muted">{{ customer.notes }}</div>
                    </div>
                  </div>
                  <button v-if="isSupervisor" class="btn btn-outline-primary btn-sm mt-2" @click="startEdit">
                    Edit
                  </button>
                </div>

                <hr />

                <!-- Order history -->
                <h2 class="h6 mb-2">Order History ({{ orders.length }})</h2>
                <div v-if="orders.length === 0" class="text-muted small">No orders for this customer.</div>
                <div v-else class="table-responsive">
                  <table class="table table-sm align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Order #</th>
                        <th>Device</th>
                        <th>Serial</th>
                        <th>Status</th>
                        <th>Started</th>
                        <th class="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="o in orders" :key="o.order_id">
                        <td>{{ o.order_id }}</td>
                        <td>{{ o.device_commonname || o.device_make }}</td>
                        <td>{{ o.serial_number }}</td>
                        <td>{{ o.order_status }}</td>
                        <td>{{ o.started_at ? new Date(o.started_at).toLocaleDateString() : "" }}</td>
                        <td class="text-end">
                          <button class="btn btn-outline-secondary btn-sm" @click="$router.push({ name: 'OrderDetail', params: { id: o.order_id } })">
                            Open
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <hr />
                <button class="btn btn-outline-secondary" @click="$router.push({ name: 'CustomersList' })">
                  Back to Customers
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";

export default {
  name: "CustomerDetail",
  data() {
    return {
      customer: null,
      orders: [],
      loading: false,
      error: null,
      editing: false,
      saving: false,
      editForm: {},
      api: new APIService(),
    };
  },
  computed: {
    isSupervisor() {
      try {
        const raw = localStorage.getItem("currentUser");
        if (!raw) return false;
        const user = JSON.parse(raw);
        const role = (user.role || "").toLowerCase();
        return role === "supervisor" || role === "admin";
      } catch {
        return false;
      }
    },
  },
  created() {
    this.fetchCustomer();
  },
  methods: {
    async fetchCustomer() {
      this.loading = true;
      this.error = null;
      try {
        const id = this.$route.params.id;
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getCustomer(id, accessToken);
        this.customer = res.data;
        this.orders = res.data.orders || [];
      } catch {
        this.error = "Unable to load customer.";
      } finally {
        this.loading = false;
      }
    },
    startEdit() {
      this.editForm = {
        customer_name: this.customer.customer_name,
        email: this.customer.email || "",
        phone: this.customer.phone || "",
        address: this.customer.address || "",
        notes: this.customer.notes || "",
      };
      this.editing = true;
    },
    async saveEdit() {
      this.saving = true;
      try {
        const accessToken = localStorage.getItem("accessToken");
        await this.api.updateCustomer(this.customer.customer_id, this.editForm, accessToken);
        this.editing = false;
        await this.fetchCustomer();
      } catch {
        this.error = "Unable to update customer.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
